import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Trash2, CircleAlert, Power, PowerOff } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BreakingNewsManager = () => {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState({
    text: '',
    text_bengali: '',
    active: true,
  });

  useEffect(() => {
    fetchBreakingNews();
  }, []);

  const fetchBreakingNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/breaking-news`, getAuthHeaders());
      setNewsList(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch breaking news', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/admin/breaking-news`, formData, getAuthHeaders());
      toast({ title: 'Success', description: 'Breaking news added successfully' });
      resetForm();
      fetchBreakingNews();
      setShowModal(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add breaking news', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this breaking news?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/breaking-news/${id}`, getAuthHeaders());
      toast({ title: 'Success', description: 'Breaking news deleted successfully' });
      fetchBreakingNews();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ text: '', text_bengali: '', active: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Breaking News Manager</h1>
          <p className="text-gray-600 mt-1">Manage urgent news alerts on homepage</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Breaking News
        </Button>
      </div>

      <div className="grid gap-4">
        {loading && newsList.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          </div>
        ) : newsList.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CircleAlert className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No breaking news. Add urgent alerts here!</p>
            </CardContent>
          </Card>
        ) : (
          newsList.map((news) => (
            <Card key={news.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge variant={news.active ? 'default' : 'secondary'} className={news.active ? 'bg-red-600' : ''}>
                        {news.active ? <Power className="w-3 h-3 mr-1" /> : <PowerOff className="w-3 h-3 mr-1" />}
                        {news.active ? 'Active' : 'Inactive'}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {new Date(news.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-lg font-semibold mb-1">{news.text_bengali}</p>
                    <p className="text-gray-600">{news.text}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(news.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Breaking News</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Headline (Bengali) *</Label>
              <Input
                value={formData.text_bengali}
                onChange={(e) => setFormData({ ...formData, text_bengali: e.target.value })}
                placeholder="জরুরি খবর..."
                required
              />
            </div>

            <div>
              <Label>Headline (English) *</Label>
              <Input
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                placeholder="Breaking news..."
                required
              />
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                <span>Show immediately on website</span>
              </label>
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={loading}>
                {loading ? 'Adding...' : 'Add Breaking News'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BreakingNewsManager;